import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Loader2 } from 'lucide-react';
import { useAuth } from './AuthContext';
import { AUTH_EXPIRED_EVENT } from './authEvents';

/**
 * ADM-131 RequireAuth layout route.
 *
 * Nest protected pages under this route so auth is enforced once via <Outlet />,
 * instead of wrapping every element in <ProtectedRoute>. Role gates:
 * AdminRoute wraps admin-only pages (e.g. /security). Dashboard role UX is
 * JWT-locked ROLE_TABS; privileged APIs enforce requireRole server-side.
 */
export const RequireAuth: React.FC = () => {
  const { user, isAuthenticated, isLoading, error } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Navigate to login as soon as the API client signals session expiry
  // (covers mid-page 401 → refresh fail without waiting for a remount).
  useEffect(() => {
    const onExpired = () => {
      navigate('/login', {
        replace: true,
        state: { from: location, reason: 'auth-expired' },
      });
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, [navigate, location]);

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[400px] gap-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <h2 className="text-xl font-semibold text-muted-foreground">
          Authenticating...
        </h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[400px] gap-4 p-4">
        <Alert variant="destructive" className="w-full max-w-[500px]">
          <AlertTitle className="text-lg font-semibold">Authentication Error</AlertTitle>
          <AlertDescription className="mt-2">{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
};

export default RequireAuth;
